"use client";

import React, { useEffect, useState, ChangeEvent, FormEvent, JSX } from "react";
import appStyle from "../styles/app.module.css";
import styles from "./ContactUs.module.css";
import images from "../constants/images";

// Type definitions
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  message: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  countryCode?: string;
  message?: string;
}

type FormFieldName = keyof FormData;

// Country codes data
const COUNTRY_CODES = [
  { code: "+91", country: "India" },
  { code: "+1", country: "United States" },
  { code: "+44", country: "United Kingdom" },
  { code: "+86", country: "China" },
  { code: "+81", country: "Japan" },
  { code: "+49", country: "Germany" },
  { code: "+33", country: "France" },
  { code: "+39", country: "Italy" },
  { code: "+7", country: "Russia" },
  { code: "+55", country: "Brazil" },
  { code: "+61", country: "Australia" },
  { code: "+82", country: "South Korea" },
  { code: "+65", country: "Singapore" },
  { code: "+971", country: "UAE" },
  { code: "+966", country: "Saudi Arabia" },
  { code: "+60", country: "Malaysia" },
  { code: "+66", country: "Thailand" },
  { code: "+62", country: "Indonesia" },
  { code: "+63", country: "Philippines" },
  { code: "+84", country: "Vietnam" },
  { code: "+52", country: "Mexico" },
  { code: "+34", country: "Spain" },
  { code: "+31", country: "Netherlands" },
  { code: "+41", country: "Switzerland" },
  { code: "+46", country: "Sweden" },
  { code: "+47", country: "Norway" },
  { code: "+45", country: "Denmark" },
  { code: "+358", country: "Finland" },
  { code: "+32", country: "Belgium" },
  { code: "+43", country: "Austria" },
  { code: "+48", country: "Poland" },
  { code: "+420", country: "Czech Republic" },
  { code: "+36", country: "Hungary" },
  { code: "+351", country: "Portugal" },
  { code: "+30", country: "Greece" },
  { code: "+90", country: "Turkey" },
  { code: "+972", country: "Israel" },
  { code: "+27", country: "South Africa" },
  { code: "+20", country: "Egypt" },
  { code: "+234", country: "Nigeria" },
  { code: "+254", country: "Kenya" },
  { code: "+212", country: "Morocco" },
  { code: "+213", country: "Algeria" },
  { code: "+216", country: "Tunisia" },
  { code: "+380", country: "Ukraine" },
  { code: "+374", country: "Armenia" },
  { code: "+995", country: "Georgia" },
  { code: "+994", country: "Azerbaijan" },
  { code: "+998", country: "Uzbekistan" },
  { code: "+996", country: "Kyrgyzstan" },
  { code: "+992", country: "Tajikistan" },
  { code: "+993", country: "Turkmenistan" },
  { code: "+977", country: "Nepal" },
  { code: "+880", country: "Bangladesh" },
  { code: "+94", country: "Sri Lanka" },
  { code: "+960", country: "Maldives" },
  { code: "+975", country: "Bhutan" },
  { code: "+93", country: "Afghanistan" },
  { code: "+98", country: "Iran" },
  { code: "+964", country: "Iraq" },
  { code: "+962", country: "Jordan" },
  { code: "+961", country: "Lebanon" },
  { code: "+963", country: "Syria" },
  { code: "+967", country: "Yemen" },
  { code: "+968", country: "Oman" },
  { code: "+973", country: "Bahrain" },
  { code: "+974", country: "Qatar" },
  { code: "+965", country: "Kuwait" },
] as const;

// Error messages configuration
const FORM_ERRORS = {
  REQUIRED: {
    firstName: "First name is required",
    lastName: "Last name is required",
    email: "Email is required",
    phoneNumber: "Phone number is required",
    message: "Message is required",
  },
  INVALID: {
    email: "Please enter a valid email address",
    phoneNumber: "Please enter a valid 10-digit mobile number",
  },
} as const;

export default function ContactUsPageContent(): JSX.Element {
  const [isMobile, setIsMobile] = useState<boolean>(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+91", // Default to India
    phoneNumber: "",
    message: "",
  });

  useEffect(() => {
    const checkMobile = (): void => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Email validation regex
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone number validation (only for Indian numbers)
  const validatePhoneNumber = (phone: string, countryCode: string): boolean => {
    // Only validate if it's an Indian number
    if (countryCode === "+91") {
      // Remove all non-digit characters for validation
      const cleanPhone = phone.replace(/\D/g, "");
      // Indian mobile numbers should be exactly 10 digits
      return cleanPhone.length === 10;
    }

    // For non-Indian numbers, just check if something is entered
    return phone.trim().length > 0;
  };

  // Handle input changes
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ): void => {
    const { name, value } = e.target;

    // Special handling for phone number to allow only digits
    if (name === "phoneNumber") {
      const digitsOnly = value.replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name as FormFieldName]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // API submission function
  const submitToAPI = async (data: FormData): Promise<boolean> => {
    try {
      const apiPayload = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone_number: `${data.countryCode}-${data.phoneNumber}`,
        message: data.message,
        source_platform: "web", // You can change this to match your platform
      };

      const response = await fetch(
        "https://api.myweddinglyinvites.com/contactUs",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(apiPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`API request failed with status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response:", result);
      return true;
    } catch (error) {
      console.error("API Error:", error);
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const newErrors: FormErrors = {};

    // Validate required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = FORM_ERRORS.REQUIRED.firstName;
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = FORM_ERRORS.REQUIRED.lastName;
    }

    if (!formData.email.trim()) {
      newErrors.email = FORM_ERRORS.REQUIRED.email;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = FORM_ERRORS.INVALID.email;
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = FORM_ERRORS.REQUIRED.phoneNumber;
    } else if (
      !validatePhoneNumber(formData.phoneNumber, formData.countryCode)
    ) {
      // Only show validation error for Indian numbers
      if (formData.countryCode === "+91") {
        newErrors.phoneNumber = FORM_ERRORS.INVALID.phoneNumber;
      }
    }

    if (!formData.message.trim()) {
      newErrors.message = FORM_ERRORS.REQUIRED.message;
    }

    setErrors(newErrors);

    // If no errors, submit the form
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);

      try {
        const success = await submitToAPI(formData);

        if (success) {
          alert("Message sent successfully!");

          // Reset form
          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            countryCode: "+91",
            phoneNumber: "",
            message: "",
          });
        } else {
          alert("Failed to send message. Please try again later.");
        }
      } catch (error) {
        console.error("Submission error:", error);
        alert("Failed to send message. Please try again later.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className={appStyle.container}>
      <div className="px-[0.25rem] md:px-[2.5rem]">
        <div className="text-center mb-4">
          <h1 className="text-4xl font-bold text-[#0B214A] mb-2">Contact Us</h1>
          <p className="text-[#303030] max-w-2xl mx-auto">
            Have questions about creating your perfect wedding invitation? We're
            here to help make your special day even more memorable.
          </p>
        </div>

        <div className="relative bg-[#ffffff] rounded-lg overflow-hidden border-12 border-[#ffffff] shadow-[0px_0px_11.6px_-1px_rgba(186,186,186,0.25)] mb-12 ">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
            {/* Contact Information Section */}
            <div className=" rounded-lg bg-pink-50 p-[1.5rem] md:p-[2rem] lg:p-10">
              <h2 className="text-2xl font-semibold text-[#1B1139]  mb-2 md:mb-6">
                Contact Information
              </h2>
              <p className="text-[#535353] mb-4 md:mb-8">
                Get in touch with our wedding invitation specialists. We're
                available to help you create the perfect invitations for your
                big day.
              </p>

              <div className="space-y-6">
                <div className="flex items-center  space-x-1 md:space-x-2">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 md:h-6 w-5 md:h-6 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <a
                    href="mailto:contact@myweddinglyinvites.com"
                    className="text-[14px] md:text-[1.1rem] hover:underline duration-200"
                  >
                    contact@myweddinglyinvites.com
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form Section */}
            <div className="lg:col-span-2  px-[2px] md:px-[2rem] py-[2rem] md:py-[2rem] lg:p-10">
              <form className="space-y-6" onSubmit={handleSubmit}>
                {/* First Row - First Name and Last Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-bold text-[#0B214A] mb-2"
                    >
                      First Name <span className="text-[#FE406C]">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter First Name"
                      disabled={isSubmitting}
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#FE406C] focus:border-transparent outline-none transition-colors ${
                        errors.firstName ? "border-red-500" : "border-gray-300"
                      } ${
                        isSubmitting ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.firstName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-bold text-[#0B214A] mb-2"
                    >
                      Last Name <span className="text-[#FE406C]">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter Last Name"
                      disabled={isSubmitting}
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#FE406C] focus:border-transparent outline-none transition-colors ${
                        errors.lastName ? "border-red-500" : "border-gray-300"
                      } ${
                        isSubmitting ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Second Row - Email and Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-bold text-[#0B214A] mb-2"
                    >
                      Email <span className="text-[#FE406C]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter Email"
                      disabled={isSubmitting}
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#FE406C] focus:border-transparent outline-none transition-colors ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      } ${
                        isSubmitting ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="phoneNumber"
                      className="block text-sm font-bold text-[#0B214A] mb-2"
                    >
                      Phone Number <span className="text-[#FE406C]">*</span>
                    </label>
                    <div className="flex">
                      {/* Country Code Dropdown */}
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={`px-1 py-3 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-[#FE406C] focus:border-transparent outline-none transition-colors bg-white ${
                          isSubmitting ? "bg-gray-100 cursor-not-allowed" : ""
                        }`}
                      >
                        {COUNTRY_CODES.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.code}
                          </option>
                        ))}
                      </select>

                      {/* Phone Number Input */}
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="Enter Phone Number"
                        disabled={isSubmitting}
                        className={`flex-1 px-4 py-3 border-t border-r border-b rounded-r-md focus:ring-2 focus:ring-[#FE406C] focus:border-transparent outline-none transition-colors ${
                          errors.phoneNumber
                            ? "border-red-500"
                            : "border-gray-300"
                        } ${
                          isSubmitting ? "bg-gray-100 cursor-not-allowed" : ""
                        }`}
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    </div>
                    {errors.phoneNumber && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.phoneNumber}
                      </p>
                    )}
                  </div>
                </div>

                {/* Message Field */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-bold text-[#0B214A] mb-2"
                  >
                    Message <span className="text-[#FE406C]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Write your message..."
                    disabled={isSubmitting}
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#FE406C] focus:border-transparent outline-none transition-colors resize-vertical ${
                      errors.message ? "border-red-500" : "border-gray-300"
                    } ${isSubmitting ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`font-medium py-3 px-8 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#FE406C] focus:ring-offset-2 ${
                      isSubmitting
                        ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                        : "bg-[#FE406C] text-white hover:bg-[#e5396b]"
                    }`}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
